import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
  type CollectionReference,
  type DocumentData,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import type { MetricRangeValue } from "@/features/dashboard/shell";

import { mapMeddreamHourlyDoc } from "../mappers/meddream-hourly";
import type { IMeddreamHourlyMetric } from "../schemas/meddream-hourly";

const COLLECTION = "meddream_hourly_metrics";

function getCollection(): CollectionReference<DocumentData> {
  return collection(db, COLLECTION);
}

function isPermissionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const code = "code" in error ? String((error as { code?: string }).code) : "";
  return (
    code === "permission-denied" ||
    code.includes("permission") ||
    (error instanceof Error && /permission/i.test(error.message))
  );
}

export class MeddreamMetricsPermissionError extends Error {
  constructor(message = "Sin permiso para leer métricas de MedDream en Firestore") {
    super(message);
    this.name = "MeddreamMetricsPermissionError";
  }
}

function wrapError(error: unknown, fallback: string): never {
  if (isPermissionError(error)) {
    throw new MeddreamMetricsPermissionError();
  }
  throw new Error(
    `${fallback}: ${error instanceof Error ? error.message : "Error desconocido"}`
  );
}

/**
 * Lee el documento de una hora puntual (docId = YYYY-MM-DDTHH).
 */
export async function getMeddreamHourById(
  docId: string
): Promise<IMeddreamHourlyMetric | null> {
  try {
    const snap = await getDoc(doc(db, COLLECTION, docId));
    if (!snap.exists()) return null;
    return mapMeddreamHourlyDoc(snap.id, snap.data());
  } catch (error) {
    wrapError(error, "Error al leer métrica horaria MedDream");
  }
}

/**
 * Query read-only según rango del hub.
 * Preferimos hourStartMs (cubre “hoy” local que cruza medianoche UTC).
 */
export async function queryMeddreamHourlyMetrics(
  range: MetricRangeValue
): Promise<IMeddreamHourlyMetric[]> {
  try {
    const col = getCollection();

    if (range.startMs != null && range.endMs != null) {
      const q = query(
        col,
        where("hourStartMs", ">=", range.startMs),
        where("hourStartMs", "<", range.endMs),
        orderBy("hourStartMs", "asc")
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => mapMeddreamHourlyDoc(d.id, d.data()));
    }

    // Fallback legacy (sin start/end): día o mes por campos UTC del doc.
    if (range.date) {
      const q = query(col, where("date", "==", range.date), orderBy("hour", "asc"));
      const snap = await getDocs(q);
      return snap.docs.map((d) => mapMeddreamHourlyDoc(d.id, d.data()));
    }

    if (range.yearMonth) {
      const q = query(
        col,
        where("yearMonth", "==", range.yearMonth),
        orderBy("hourStartMs", "asc")
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => mapMeddreamHourlyDoc(d.id, d.data()));
    }

    return [];
  } catch (error) {
    wrapError(error, "Error al consultar métricas horarias MedDream");
  }
}
